import { forwardRef, type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils.js';

/* ===========================================================================
 * Card — `rounded-panel` (16px) + a hairline border + `shadow-resting`, with
 * `p-card` (20px) carried by the header/content/footer sections rather than
 * the shell, so a card can still hold a full-bleed child (a table, an image)
 * without fighting the padding.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS SAYS `rounded-panel` AND NOT `rounded-card`.
 *
 * It looks backwards and it is not. The radius ROLES were re-authored for the
 * warm-paper system (docs/design-rules.md §1) and two of them changed meaning
 * rather than value:
 *
 *   --radius-card    8px → 12px   now: INNER card, nested panel
 *   --radius-panel  12px → 16px   now: CARD, tile, table shell
 *
 * So the role named `card` is no longer the role a card wants. That is an
 * unfortunate collision and it is worth the confusion here, because the
 * alternative was renaming a role that ~78 call sites reference. The important
 * consequence: **a top-level card is `rounded-panel` and a panel nested inside
 * one is `rounded-card`** — 16px outside, 12px inside, which is the nesting step
 * the mocks draw. See §4c of docs/design-system-migration.md.
 *
 * ---------------------------------------------------------------------------
 * THE BORDER IS NOT DECORATIVE ANY MORE.
 *
 * The page is `#FBFAF8` and a card is `#FFFFFF` — a 1.043:1 step, which no eye
 * resolves on its own. That was a deliberate decision (§0 of the migration doc)
 * and it moves the whole job of separating a card from the page onto the
 * hairline plus `shadow-resting`. So `variant="elevated"` losing its border is
 * now a real cost rather than a style choice, and a card with neither a border
 * nor a shadow does not read as a card at all.
 *
 * WHICH hairline is `border-subtle` and not `border`. The handoff draws a
 * CONTAINER's edge one step lighter than a CONTROL's — every card in the nine
 * mocks is `1px solid #E7E3DB` (e.g. `Hadir Employee.dc.html:209`) while the
 * button, the input and the chip beside it take #DDD8CE. Those were one value
 * until `--border-subtle` was split out, which drew every card edge in the
 * console a step harder than the design asked for. See the Boundaries note in
 * apps/web/src/index.css for the full ramp.
 *
 * The sister app's two sources disagreed on the resting shadow, and the
 * conclusion survives the re-authoring: on a console screen carrying eight or
 * ten cards, a heavy shadow on every one reads as clutter. `default` takes the
 * flat `shadow-resting`; `elevated` takes the lift that the rest of the system
 * uses for hover, which is the only "raised" weight the four-role ramp has.
 * ======================================================================== */
/* ---------------------------------------------------------------------------
 * THE SECTION PADDING — `p-4 md:p-card` (16px phone, 20px from `md` up).
 *
 * `--pad-card` moved 24px → 20px with the warm-paper system: §1 of the rules
 * puts card padding at 18–22px, and 24 sat outside that band. The two-value
 * responsive pattern below is unchanged; only the desktop step moved, which
 * narrows the phone→desktop jump from 8px to 4px and makes the `md:` half less
 * visible than it used to be. It still earns its keep on a 375px screen.
 *
 * It used to be a flat `p-card`, and the app disagreed with it 123 times out of
 * 156: `p-4` ×57, `p-0` ×18 (a flush table — legitimate), `p-6` ×11 (24px, a
 * pure restatement of `p-card`), `p-3` ×9, `p-5`/`p-tile` ×3, `p-8`/`p-10` ×2.
 * Seven inner paddings shipped. 16px was the de-facto value and 24px was what
 * the primitive insisted on, which is the two-value responsive pattern the
 * token file already names: design-tokens.css, at `--pad-tile`, writes
 * "their phone step is 16px, which is stock `p-4`" — `p-4 md:p-tile` — and
 * ControlBar already follows it. This is the same pattern at the card step.
 *
 * NOW REDUNDANT AT CALL SITES (delete the className, do not "fix" it here):
 *   `p-4`  — identical below `md`, and 24px above it is the upgrade.
 *   `p-6`  — always was `p-card` spelled differently.
 * STILL MEANINGFUL: `p-0` (a table or image that must reach the card's edge),
 * and any deliberate off-scale value (`p-3`, `p-8`, `p-10`).
 *
 * ---------------------------------------------------------------------------
 * WHY THE `md:` STEP IS DROPPED WHEN A CALL SITE SETS PADDING.
 *
 * A media-queried default cannot be overridden by an unprefixed utility, and
 * tailwind-merge will not save you: `p-0` and `md:p-card` carry different
 * modifiers, so merge keeps BOTH, and Tailwind emits every unprefixed utility
 * before every `md:` one. Verified against the real config — `.p-0` lands at
 * line 1000 of the compiled sheet and `@media (min-width:768px){.md\:p-card}`
 * at 1806. So a bare `<CardContent className="p-0">` would silently regain
 * 24px of padding at ≥768px, on all 18 of the flush tables.
 *
 * The rule that fixes it is also the honest one: THE MOMENT A CALL SITE SAYS
 * ANYTHING ABOUT PADDING, IT OWNS PADDING AT EVERY WIDTH. The `md:` half steps
 * aside and the base half is left for tailwind-merge to resolve exactly as the
 * flat `p-card` did before. `p-0` stays flush, `p-3` stays 12, `pt-card` still
 * gets its top padding back, and nothing changes at one width and not another.
 *
 * Reading the className to decide is the same technique `Select.tsx` uses for
 * `splitOuterBoxClasses`, for the same reason: a caller's classes and a
 * primitive's defaults do not compose through CSS alone.
 * ------------------------------------------------------------------------ */
const PADDING_UTILITY = /^-?(?:p|px|py|ps|pe|pt|pr|pb|pl)-/;

/** True if `className` contains any padding utility, at any variant. */
function saysPadding(className: string | undefined): boolean {
  return (className ?? '')
    .split(/\s+/)
    .some(
      (token) =>
        token !== '' &&
        PADDING_UTILITY.test(token.replace(/^!/, '').split(':').pop() ?? '')
    );
}

/**
 * `pt-0` on `flush` sections (content, footer) so they sit directly under a
 * header without doubling the gap — the same reason the flat default carried it.
 */
function sectionPadding(className: string | undefined, flush: boolean): string {
  return cn(
    'p-4',
    // `pt-0` exists to stop a section DOUBLING the gap under a CardHeader — it
    // was never meant to strip the top off a card that has no header. It only
    // looked harmless because ~26 call sites passed `className="p-4"`, which
    // beats `pt-0` in tailwind-merge and quietly handed the top padding back:
    //   twMerge('p-4 pt-0 p-4') -> 'p-4'
    // So deleting those now-redundant overrides — which is exactly what the
    // move to a `p-4 md:p-card` default invites — would have flattened the top
    // of every headerless card in the app, and the diff would have read as
    // pure cleanup.
    //
    // `:not(:first-child)` states the real rule instead: zero the top only when
    // something (the header) is already above me.
    flush && '[&:not(:first-child)]:pt-0',
    !saysPadding(className) &&
      cn('md:p-card', flush && 'md:[&:not(:first-child)]:pt-0')
  );
}

const cardVariants = cva('bg-surface text-foreground rounded-panel', {
  variants: {
    variant: {
      default: 'border border-border-subtle shadow-resting',
      // Keeps the hairline, unlike before. With a 1.043:1 page/card step the
      // border is what says "card"; a shadow alone leaves the top edge — the one
      // a reader scanning down the page meets first — with nothing on it.
      elevated: 'border border-border-subtle shadow-hover',
      outlined: 'border border-border-subtle',
    },
  },
  defaultVariants: { variant: 'default' },
});

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant }), className)}
      {...props}
    />
  )
);
Card.displayName = 'Card';

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      // Header, content and footer share ONE padding rule. If the header stayed
      // at a flat 24px while the content stepped down to 16 on a phone, the two
      // would no longer share an inner rail and every card would read as
      // misaligned at exactly the width where that is most obvious.
      //
      // THE BOTTOM IS THE ONE EDGE THAT IS NOT THE CARD'S PADDING. It is the
      // step between a heading and the body under it, and the handoff draws
      // that step at `--gap-component` (16px) while the card's own inset is
      // 20px — `padding: 20px 22px` on the card with the field grid at
      // `margin-top: 16px` (Hadir Employee.dc.html:272-274, and identically at
      // :310, :348, :386). `CardContent` closes up to the header with
      // `pt-0`, so the header's bottom padding IS that step, and at 20px it
      // read 4px loose on every card on the page.
      //
      // Only the `md:` half needs saying: the phone step is already `p-4`
      // (16px), which is the number. And like the `md:` half of the padding
      // itself it steps aside the moment a call site sets padding — see the
      // note above sectionPadding on why a caller owns padding at every width.
      className={cn(
        'flex flex-col gap-element',
        sectionPadding(className, false),
        !saysPadding(className) && 'md:pb-component',
        className
      )}
      {...props}
    />
  )
);
CardHeader.displayName = 'CardHeader';

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    // `text-h3` = 17px/600 at −0.3px in the display face — exactly the "card
    // heading" role in docs/design-rules.md §1, and what every mocked card
    // opens with. It carries its own family, weight and tracking, so no
    // `font-*` here.
    <h3 ref={ref} className={cn('text-h3', className)} {...props} />
  )
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn('text-copy-sm text-muted-foreground', className)}
      {...props}
    />
  )
);
CardDescription.displayName = 'CardDescription';

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    // Deliberately sets no font size: card bodies hold tables, forms and prose
    // that own their own type, and an inherited size here would quietly
    // out-rank whichever of them forgot to set one.
    <div ref={ref} className={cn(sectionPadding(className, true), className)} {...props} />
  )
);
CardContent.displayName = 'CardContent';

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center gap-element', sectionPadding(className, true), className)}
      {...props}
    />
  )
);
CardFooter.displayName = 'CardFooter';
